import { FC, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { faCheck, faShareFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ActionIcon, CopyButton, Group, Tooltip } from "@mantine/core";

import { Issue, IssueType } from "@protos/issues/v1/issues";
import {
  HoverCopyButtonTextProps,
  PageShell,
  TopSectionProps,
} from "@shared/components";
import {
  useBasePath,
  useIsMobile,
  useOrg,
  useProductConfig,
} from "@shared/hooks";
import { fileName, formatFilePath } from "@shared/utils";

import { AssistantButton } from "../AssistantDialog";
import { InlineTriageButton } from "../InlineTriageButton";
// Need to import these components using relative paths because of weird React preloadable errors
import { SastFindingInfoIconList } from "../SastFindingInfoIconList";
import { ScaFindingInfoIconList } from "../ScaFindingInfoIconList";
import { SecretsFindingInfoIconList } from "../SecretsFindingInfoIconList";

const TITLE_MAP: Record<IssueType, string> = {
  [IssueType.SCA]: "Supply Chain",
  [IssueType.SAST]: "Code",
  [IssueType.SECRETS]: "Secrets",
  [IssueType.RESEARCH]: "",
  [IssueType.UNSPECIFIED]: "",
};

const HREF_MAP: Record<IssueType, string> = {
  [IssueType.SCA]: "/supply-chain",
  [IssueType.SAST]: "/findings",
  [IssueType.SECRETS]: "/secrets",
  [IssueType.RESEARCH]: "",
  [IssueType.UNSPECIFIED]: "",
};

const ICON_LIST_COMPONENT_MAP: Record<
  IssueType,
  FC<{ finding: Issue }> | null
> = {
  [IssueType.SCA]: ScaFindingInfoIconList,
  [IssueType.SAST]: SastFindingInfoIconList,
  [IssueType.SECRETS]: SecretsFindingInfoIconList,
  [IssueType.RESEARCH]: null,
  [IssueType.UNSPECIFIED]: null,
};

export interface FindingPageShellProps {
  finding: Issue;
  children?: React.ReactNode;
}

/**
 * A page shell component for the finding details page
 * @param findingData - the data for the current finding
 */
export const FindingPageShell: FC<FindingPageShellProps> = ({
  finding,
  children,
}) => {
  const [org] = useOrg();
  const isMobile = useIsMobile();
  const { data: productConfig } = useProductConfig();
  const { state: locationState } = useLocation<{ params?: string }>();

  const basePath = useBasePath();

  const search = useMemo(() => {
    if (locationState && locationState.params) {
      return `?${locationState.params}`;
    }
    return "";
  }, [locationState]);

  const topSectionProps: TopSectionProps = {
    breadcrumbs: [
      {
        title: TITLE_MAP[finding.issueType],
        href: `${basePath}${HREF_MAP[finding.issueType]}${search}`,
      },
      {
        title: "#" + finding.id.toString(),
      },
    ],
  };

  const filePath = finding.filePath ?? "";

  const hoverCopyButtonTextProps: HoverCopyButtonTextProps = {
    copyText: `${filePath}:${finding.line}`,
    tooltipText: "Copy full path",
  };

  const FindingInfoIconList = ICON_LIST_COMPONENT_MAP[finding.issueType];

  return (
    <PageShell
      title={
        isMobile
          ? `${fileName(filePath)}:${finding.line}`
          : `${formatFilePath(filePath)}:${finding.line}`
      }
      titleUrl={finding.lineOfCodeUrl}
      hoverCopyButtonTextProps={hoverCopyButtonTextProps}
      topSectionProps={topSectionProps}
      rightSection={
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          <Group wrap="nowrap">
            <CopyButton value={window.location.toString()} timeout={1200}>
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? "Copied" : "Copy link to this page"}
                  position="left"
                >
                  <ActionIcon
                    color={copied ? "teal" : "blue"}
                    onClick={copy}
                    variant="transparent"
                  >
                    <FontAwesomeIcon
                      icon={copied ? faCheck : faShareFromSquare}
                      onClick={() =>
                        navigator.clipboard.writeText(
                          window.location.toString()
                        )
                      }
                    />
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
            {finding.issueType === IssueType.SAST &&
              finding.autotriage === undefined && (
                <AssistantButton
                  deploymentId={org!.id}
                  numSelected={1}
                  productConfig={productConfig}
                  selectedFindingIds={new Set<number>([finding.id])}
                  filterOverrides={undefined}
                  showAnalyzeNumber={false}
                  buttonSize="sm"
                />
              )}
            <InlineTriageButton issue={finding} issueType={finding.issueType} />
          </Group>
        </>
      }
      // not passing a fragment because FindingInfoIconList has its own
      // custom spacing
      bottomSection={
        FindingInfoIconList ? <FindingInfoIconList finding={finding} /> : null
      }
      withBorder={false}
    >
      {children}
    </PageShell>
  );
};
